<script lang="ts">
import type { Cell, Grid } from "@/logic/GridBuilder"
import type { TableCellProperties } from "@kaleidoscope-solutions/engine-wasm"
import type { CSSProperties } from "vue"
import { setCellClassByCell } from "@/utils/table"
import { CellDisplayKind, CellKind } from "@kaleidoscope-solutions/engine-wasm"
import { toRefs } from "@vueuse/core"
import { toRef } from "vue"

export interface GridProps {
  name: string
  grid: Grid
  selectedCell?: Cell
  // Set the cell key to use to display the content of the cell
  valueKey?: (keyof TableCellProperties)
}
</script>

<script lang="ts" setup>
interface Emits {
  (event: "cellSelected", cell: Cell): void
}

const props = withDefaults(defineProps<GridProps>(), {})

const emit = defineEmits<Emits>()

const { grid } = toRefs(props)

const valueKey = toRef(props, "valueKey", "formattedValue")

function selectedCellCssClasses(selectedCell: Cell, cell: Cell): string[] {
  const classes = []
  const sameRow = selectedCell.row === cell.row
  const sameColumn = selectedCell.column === cell.column
  const isColumnHeader = cell.displayKind === CellDisplayKind.ColumnHeader
  const colSpan = cell?.colspan ?? 0
  const inColSpanRange =
    selectedCell.column !== undefined &&
    selectedCell.column >= cell.column &&
    selectedCell.column < cell.column + colSpan

  const colRangeLast =
    selectedCell.column !== undefined && cell.column === selectedCell.column + colSpan - 1
  const rowSpan = cell?.rowspan ?? 0

  const inRowSpanRange =
    selectedCell.row !== undefined &&
    selectedCell.row >= cell.row &&
    selectedCell.row < cell.row + (cell?.rowspan ?? 0)

  const rowRangeLast =
    selectedCell.row !== undefined && cell.row === selectedCell.row + rowSpan - 1

  // Select the current cell.
  if (sameRow && sameColumn) {
    classes.push("selected-cell--cell")
  }

  // not nested & Select the current column.
  if (colSpan === 1 && sameColumn) {
    classes.push("selected-cell--column")
  }

  // for nested cells
  if (inColSpanRange) {
    classes.push("selected-cell--nested--col")

    // start of range
    if (sameColumn) {
      classes.push("selected-cell--nested--col-start")
    }
    // end of range
    if (colRangeLast) {
      classes.push("selected-cell--nested--col-end")
    }
  }

  // for nested ROWS
  if (inRowSpanRange) {
    classes.push("selected-cell--nested--row")

    // start of range
    if (sameRow) {
      classes.push("selected-cell--nested--row-start")
    }
    // end of range
    if (rowRangeLast) {
      classes.push("selected-cell--nested--row-end")
    }
  }

  // Style column headers different.
  if (sameColumn && isColumnHeader) {
    classes.push("selected-cell--column-header")
  }

  // If body row is selected.
  if (sameRow) {
    classes.push("selected-cell--row")
  }

  return classes
}

function cellClassMapper(cell: Cell) {
  // Set a class based on the display kind.
  const cellClasses = [`cell--${CellDisplayKind[cell.displayKind]}`]

  if (cell.cellKind !== undefined) {
    cellClasses.push(`cell--${CellKind[cell.cellKind]}`)
  }

  if (cell.valueType) {
    cellClasses.push(`cell--vt-${cell.valueType}`)
  }

  if (cell.emptyRelationship) {
    cellClasses.push("cell--emptyRelationship")
  }

  // Set class for read only.
  if (cell.readOnly) {
    cellClasses.push("cell--readOnly")
  }

  if (props.selectedCell) {
    cellClasses.push(...selectedCellCssClasses(props.selectedCell, cell))
  }

  cellClasses.push(...setCellClassByCell(cell))

  return cellClasses.join(" ")
}

const cellHeight = 34 // 32px cell height and border

function cellStyle(cell: Cell) {
  const style: CSSProperties = {}

  if (cell.rowspan > 1) {
    style.gridRow = `span ${cell.rowspan}`
  }

  if (cell.colspan > 1) {
    style.gridColumn = `span ${cell.colspan}`
  }

  if (
    cell.displayKind === CellDisplayKind.ColumnHeader ||
    (cell.displayKind === CellDisplayKind.GroupColumnHeader && cell.cellKind !== CellKind.DatasetValue) ||
    (cell.displayKind === CellDisplayKind.SummaryCell && cell.cellKind === CellKind.DatasetName)
  ) {
    // Offset column to the top as sticky items aren't stackable.
    style.top = `${cell.row * cellHeight - 16}px`
    style.position = "sticky"
    style.zIndex = 2
  }

  return style
}

function handleCellClick(cell: Cell) {
  emit("cellSelected", cell)
}
</script>

<template>
  <div
    v-if="grid"
    class="inline-grid text-sm"
    :style="`grid-template-columns: repeat(${grid.colCount}, max-content);`"
  >
    <!-- Limit rows for performance reasons -->
    <template v-for="row in grid">
      <!-- row start -->
      <!-- Limit columns for performance reasons -->
      <template
        v-for="cell in row"
        :key="`${cell.id}-${cell.row}-${cell.column}-${cell.formattedValue}-${cell.displayKind}`"
      >
        <div
          v-if="!cell.hiddenBySpan"
          :style="cellStyle(cell)"
          class="group/cell"
          :class="cellClassMapper(cell)"
          :data-test="`cell-${cell.row}-${cell.column}`"
          @click="handleCellClick(cell)"
        >
          <template v-if="cell.displayKind === CellDisplayKind.Gap" />
          <template
            v-else-if="cell.displayKind === CellDisplayKind.SummaryCell"
          >
            <slot
              name="summary"
              :cell="cell"
            >
              <div class="px-2 py-1.5">
                {{ cell[valueKey] }}
              </div>
            </slot>
          </template>
          <template
            v-else-if="cell.displayKind === CellDisplayKind.GroupTypeLabel"
          >
            <slot
              name="group-label"
              :cell="cell"
            >
              <div class="px-2 pt-1">
                {{ cell[valueKey] }}
              </div>
            </slot>
          </template>
          <template
            v-else-if="cell.displayKind === CellDisplayKind.GroupColumnHeader"
          >
            <slot
              name="group-column-header"
              :cell="cell"
            >
              <div class="px-2 py-1.5">
                {{ cell[valueKey] }}
              </div>
            </slot>
          </template>
          <template
            v-else-if="cell.displayKind === CellDisplayKind.GroupRowHeader"
          >
            <slot
              name="group-row-header"
              :cell="cell"
            >
              <div class="px-2 py-1.5">
                {{ cell[valueKey] }}
              </div>
            </slot>
          </template>
          <template
            v-else-if="cell.displayKind === CellDisplayKind.ColumnHeader"
          >
            <slot
              name="column-header"
              :cell="cell"
            >
              <div class="px-2 py-1.5">
                {{ cell[valueKey] }}
              </div>
            </slot>
          </template>
          <template v-else>
            <slot :cell="cell">
              <div class="px-2 py-1.5">
                {{ cell[valueKey] }}
              </div>
            </slot>
          </template>
        </div>
      </template>
      <!-- row end -->
    </template>
  </div>
</template>
